import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Container, Typography, Card, CardContent, Grid, CircularProgress,
  Select, MenuItem, FormControl, InputLabel, Button, Tooltip, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Snackbar,
  Alert, Tabs, Tab, Chip, InputAdornment,
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon,
  School as SchoolIcon, Person as PersonIcon,
  CalendarMonth as CalendarIcon, Refresh as RefreshIcon,
  AccessTime as TimeIcon, MeetingRoom as RoomIcon,
  Check as CheckIcon, Close as CloseIcon, Warning as WarningIcon,
  PictureAsPdf as PdfIcon,
} from '@mui/icons-material';
import LocationOnIcon from '@mui/icons-material/LocationOn';

// ─────────────────────────────────────────────────────────────────────────────
// Design tokens
// ─────────────────────────────────────────────────────────────────────────────
const C = {
  navy:    '#0B1F3D',
  navyMid: '#163060',
  blue:    '#1976D2',
  blueSoft:'#EBF3FD',
  border:  '#DDE3EC',
  surface: '#F4F6FB',
  white:   '#FFFFFF',
  text:    '#1E293B',
  muted:   '#64748B',
  faint:   '#94A3B8',
  green:   '#2E7D32',
  red:     '#C62828',
  orange:  '#E65100',
};

// Slot colour palette — one colour per unique subject name
const PALETTES = [
  { bg: '#EDE7F6', border: '#B39DDB', text: '#4527A0' },
  { bg: '#E3F2FD', border: '#90CAF9', text: '#0D47A1' },
  { bg: '#E8F5E9', border: '#A5D6A7', text: '#1B5E20' },
  { bg: '#FFF8E1', border: '#FFE082', text: '#E65100' },
  { bg: '#FCE4EC', border: '#F48FB1', text: '#880E4F' },
  { bg: '#E0F7FA', border: '#80DEEA', text: '#006064' },
  { bg: '#FBE9E7', border: '#FFAB91', text: '#BF360C' },
  { bg: '#F9FBE7', border: '#E6EE9C', text: '#558B2F' },
  { bg: '#E8EAF6', border: '#9FA8DA', text: '#283593' },
  { bg: '#E0F2F1', border: '#80CBC4', text: '#004D40' },
  { bg: '#FFF3E0', border: '#FFCC80', text: '#BF360C' },
  { bg: '#F3E5F5', border: '#CE93D8', text: '#4A148C' },
];
const paletteCache = {};
let pidx = 0;
const getPalette = (name) => {
  if (!name) return { bg: '#F1F5F9', border: '#CBD5E1', text: '#475569' };
  if (!paletteCache[name]) { paletteCache[name] = PALETTES[pidx++ % PALETTES.length]; }
  return paletteCache[name];
};

// ─────────────────────────────────────────────────────────────────────────────
// API helpers
// ─────────────────────────────────────────────────────────────────────────────
const BASE  = process.env.REACT_APP_BASE_URL;
const tok   = () => sessionStorage.getItem('token');
const empId = () => sessionStorage.getItem('employeeId') || 0;

const apiGet = (path) =>
  fetch(`${BASE}${path}`, { headers: { Authorization: tok() } });

const apiPost = (path, body = {}) =>
  fetch(`${BASE}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: tok() },
    body: JSON.stringify(body),
  });

const apiPut = (path, body = {}) =>
  fetch(`${BASE}${path}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', Authorization: tok() },
    body: JSON.stringify(body),
  });

const apiDel = (path) =>
  fetch(`${BASE}${path}`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json', Authorization: tok() },
  });

const safeJson = async (res) => {
  try { return await res.json(); } catch { return null; }
};

// ─────────────────────────────────────────────────────────────────────────────
// Helper — read field keys from API objects
// Days dropdown  → { dayId, dayName }
// Timetable rows → { daysId, dayName, periodId, startTime, endTime, ... }
// Periods dropdown → { periodId, sequenceNo, startTime, endTime }  (NO periodName)
// ─────────────────────────────────────────────────────────────────────────────
const getDayId    = (d) => d.dayId    || d.daysId    || d.DaysId    || d.dayOfWeekId || '';
const getDayLabel = (d) => d.dayName  || d.DayName   || '';
const getPerId    = (p) => p.periodId || p.PeriodId  || '';
// Periods API has no periodName — use sequenceNo-based label
const getPerLabel = (p) =>
  p.periodName || p.PeriodName ||
  (p.sequenceNo != null ? `Period ${p.sequenceNo}` : `Period ${getPerId(p)}`);
const getPerTime  = (p) => {
  const s = p.startTime || p.StartTime || '';
  const e = p.endTime   || p.EndTime   || '';
  return s && e ? `${s} – ${e}` : null;
};

// ─────────────────────────────────────────────────────────────────────────────
// SlotCard — renders ONE timetable entry inside a cell
// ─────────────────────────────────────────────────────────────────────────────
const SlotCard = ({ slot, onEdit, onDelete, showClass, canEdit, canDelete }) => {
  const subjectName = slot.subjectName || slot.SubjectName || '';
  const teacherName = slot.teacherName || slot.TeacherName || '';
  const isProxy          = (slot.isProxyAssigned ?? slot.IsProxyAssigned) === 'Yes';
const originalTeacher  = slot.originalTeacherName || slot.OriginalTeacherName || '';
  const roomId      = slot.roomId      || slot.RoomId      || slot.roomNo || 0;
  const pal         = getPalette(subjectName);
  const ttId        = slot.timetableId || slot.TimeTableId || 0;
  // In teacher-view show class+section so teacher knows which class
  const classLabel  = showClass
    ? `${slot.className || ''} ${slot.sectionName || ''}`.trim()
    : '';

  return (
    <Box sx={{
      borderRadius: '10px',
      bgcolor: pal.bg,
      border: `1.5px solid ${pal.border}`,
      p: '7px 9px',
      position: 'relative',
      transition: 'all 0.18s',
      '&:hover': {
        boxShadow: `0 4px 14px ${pal.border}99`,
        transform: 'translateY(-1px)',
        '& .slot-actions': { opacity: 1 },
      },
    }}>
      <Typography sx={{ fontWeight: 800, color: pal.text, fontSize: '0.75rem', lineHeight: 1.25, mb: 0.3, pr: 3.5 }}>
        {subjectName}
      </Typography>
     {isProxy ? (
  <Typography sx={{ color: C.muted, fontSize: '0.67rem', lineHeight: 1.3, textDecoration: 'line-through' }}>
    {originalTeacher}
  </Typography>
) : null}
<Typography sx={{ color: C.muted, fontSize: '0.67rem', lineHeight: 1.3 }}>
  {teacherName}
</Typography>
{isProxy ? (
  <Chip label="Proxy" size="small" sx={{ height: 16, fontSize: '0.6rem', bgcolor: '#FFF3E0', color: '#E65100', fontWeight: 700, mt: 0.3 }} />
) : null}
      {classLabel ? (
        <Typography sx={{ color: C.blue, fontSize: '0.62rem', fontWeight: 600, mt: 0.3 }}>
          {classLabel}
        </Typography>
      ) : null}
      {roomId && roomId !== 0 ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3, mt: 0.4 }}>
          <RoomIcon sx={{ fontSize: 10, color: C.faint }} />
          <Typography sx={{ color: C.faint, fontSize: '0.61rem' }}>Room {roomId}</Typography>
        </Box>
      ) : null}

      <Box className="slot-actions" sx={{
        position: 'absolute', top: 3, right: 3,
        opacity: 0, transition: 'opacity 0.15s',
        display: 'flex', flexDirection: 'column', gap: 0.3,
      }}>
        {canEdit && (
          <Tooltip title="Edit" placement="left">
            <IconButton size="small"
              onClick={e => { e.stopPropagation(); onEdit(slot); }}
              sx={{ p: 0.4, width: 21, height: 21, bgcolor: 'rgba(255,255,255,0.92)', '&:hover': { bgcolor: '#fff', color: C.blue } }}>
              <EditIcon sx={{ fontSize: 11 }} />
            </IconButton>
          </Tooltip>
        )}
        {/* {canDelete && (
          <Tooltip title="Delete" placement="left">
            <IconButton size="small"
              onClick={e => { e.stopPropagation(); onDelete(ttId); }}
              sx={{ p: 0.4, width: 21, height: 21, bgcolor: 'rgba(255,255,255,0.92)', '&:hover': { bgcolor: '#FFEBEE', color: C.red } }}>
              <DeleteIcon sx={{ fontSize: 11, color: C.red }} />
            </IconButton>
          </Tooltip>
        )} */}
      </Box>
    </Box>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// SlotCell — wraps one or more SlotCards + empty-state add button
// ─────────────────────────────────────────────────────────────────────────────
const SlotCell = ({ slots, onEdit, onDelete, onAdd, dayId, periodId, canAdd, canEdit, canDelete, showClass }) => {
  if (!slots || slots.length === 0) {
    return (
      <Box
        onClick={() => canAdd && onAdd(dayId, periodId)}
        sx={{
          minHeight: 76, borderRadius: '10px',
          border: `1.5px dashed ${C.border}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          bgcolor: '#FAFBFD', cursor: canAdd ? 'pointer' : 'default',
          transition: 'all 0.18s',
          '&:hover': canAdd
            ? { bgcolor: C.blueSoft, borderColor: C.blue, '& .add-icon': { color: C.blue, transform: 'scale(1.2)' } }
            : {},
        }}
      >
        {canAdd && <AddIcon className="add-icon" sx={{ color: '#C8D5E8', fontSize: 22, transition: 'all 0.18s' }} />}
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
      {slots.map((slot) => (
        <SlotCard
          key={slot.timetableId || slot.TimeTableId}
          slot={slot}
          onEdit={onEdit}
          onDelete={onDelete}
          showClass={showClass}
          canEdit={canEdit}
          canDelete={canDelete}
        />
      ))}
    </Box>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// PDF generator
// ─────────────────────────────────────────────────────────────────────────────
const generatePDF = ({ title, days, periods, slots }) => {
  const iframe = document.createElement('iframe');
  iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0';
  document.body.appendChild(iframe);

  // Build a map: `daysId_periodId` → array of slots
  const slotMap = {};
  slots.forEach(s => {
    const dId = String(s.daysId || s.DaysId || s.dayId || '');
    const pId = String(s.periodId || s.PeriodId || '');
    const key = `${dId}_${pId}`;
    if (!slotMap[key]) slotMap[key] = [];
    slotMap[key].push(s);
  });

  const rows = periods.map(period => {
    const pId    = String(getPerId(period));
    const pLabel = getPerLabel(period);
    const pTime  = getPerTime(period) || '';
    const cells  = days.map(d => {
      const dId  = String(getDayId(d));
      const arr  = slotMap[`${dId}_${pId}`] || [];
      const inner = arr.length
        ? arr.map(s =>
            `<div style="margin-bottom:4px">
               <strong style="color:#0B1F3D;font-size:11px">${s.subjectName || ''}</strong><br/>
               <span style="color:#64748B;font-size:10px">${s.teacherName || ''}</span>
               ${s.className ? `<br/><span style="color:#1976D2;font-size:9px">${s.className} ${s.sectionName || ''}</span>` : ''}
               ${s.roomId && s.roomId !== 0 ? `<br/><span style="color:#94A3B8;font-size:9px">Room ${s.roomId}</span>` : ''}
             </div>`).join('')
        : '';
      return `<td style="padding:8px 10px;vertical-align:top;border:1px solid #DDE3EC;background:${arr.length ? '#fff' : '#FAFBFD'};font-size:11px;">${inner}</td>`;
    }).join('');

    return `<tr>
      <td style="padding:8px 10px;border:1px solid #DDE3EC;background:#F1F5F9;font-size:11px;font-weight:700;color:#0B1F3D;white-space:nowrap;">
        ${pLabel}<br/><span style="font-weight:400;color:#94A3B8;font-size:9px;">${pTime}</span>
      </td>${cells}
    </tr>`;
  }).join('');

  const dayHeaders = days.map(d =>
    `<th style="padding:10px 8px;background:#0B1F3D;color:#fff;font-size:12px;font-weight:700;border:1px solid #163060;">${getDayLabel(d)}</th>`
  ).join('');

  const html = `<!DOCTYPE html><html><head><title>${title}</title>
  <style>
    body{font-family:Arial,sans-serif;margin:0;padding:20px}
    h2{color:#0B1F3D;margin-bottom:4px;font-size:18px}
    p{color:#64748B;font-size:12px;margin:0 0 16px}
    table{border-collapse:collapse;width:100%}
    @media print{body{padding:10px}}
  </style></head><body>
    <h2>${title}</h2>
    <p>Generated on ${new Date().toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'})}</p>
    <table>
      <thead><tr>
        <th style="padding:10px 8px;background:#0B1F3D;color:#fff;font-size:12px;font-weight:700;border:1px solid #163060;">Period / Day</th>
        ${dayHeaders}
      </tr></thead>
      <tbody>${rows}</tbody>
    </table>
  </body></html>`;

  iframe.contentDocument.open();
  iframe.contentDocument.write(html);
  iframe.contentDocument.close();
  iframe.contentWindow.focus();
  setTimeout(() => {
    iframe.contentWindow.print();
    setTimeout(() => document.body.removeChild(iframe), 1000);
  }, 400);
};

const getDefaultFromDate = () => {
  const d = new Date();
  const day = d.getDay(); // 0=Sun,1=Mon,...6=Sat
  const diffToMonday = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diffToMonday);
  return d.toISOString().split('T')[0];
};

const getDefaultToDate = () => getSaturdayOfWeek(getDefaultFromDate());

const getSaturdayOfWeek = (dateStr) => {
  const d = new Date(dateStr);
  const day = d.getDay(); // 0=Sun,1=Mon,...6=Sat
  const diffToMonday = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diffToMonday + 5); // Monday + 5 = Saturday
  return d.toISOString().split('T')[0];
};

// ─────────────────────────────────────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────────────────────────────────────
export default function TimetablePage() {
  const [tab, setTab] = useState(0);

    const [loadWarning, setLoadWarning] = useState('');
    const [ttFromDate, setTtFromDate] = useState(getDefaultFromDate());
const [ttToDate,   setTtToDate]   = useState(getDefaultToDate());

  // ── Branch state (mirrors TeacherLoadReport pattern) ─────────────────────
  const [isMaster,         setIsMaster]         = useState(false);
  const [branches,         setBranches]         = useState([]);
  const [selectedBranchId, setSelectedBranchId] = useState(null);

  // ── Resolve active branchId for use in all API calls ─────────────────────
  // Master users: from dropdown selection
  // Non-master users: from sessionStorage (set at login)
  const activeBranchId = isMaster
    ? selectedBranchId
    : sessionStorage.getItem('branchId');

  // Dropdown data
  const [classes,      setClasses]      = useState([]);
  const [sections,     setSections]     = useState([]);
  const [allTeachers,  setAllTeachers]  = useState([]);
  const [formTeachers, setFormTeachers] = useState([]);
  const [formSubjects, setFormSubjects] = useState([]);
  const [days,         setDays]         = useState([]);
  const [periods,      setPeriods]      = useState([]);

  // Selections
  const [selClass,   setSelClass]   = useState('');
  const [selSection, setSelSection] = useState('');
  const [selTeacher, setSelTeacher] = useState('');

  // Timetable data
  const [classTT,   setClassTT]   = useState([]);
  const [teacherTT, setTeacherTT] = useState([]);

  const [loading,          setLoading]          = useState(false);
  const [loadingDropdowns, setLoadingDropdowns] = useState(true);

  // Modal
  const [modalOpen, setModalOpen] = useState(false);
  const [editSlot,  setEditSlot]  = useState(null);
  const [form, setForm] = useState({
    timetableId: 0, classId: '', sectionId: '',
    subjectId: '', teacherId: '', roomNo: '',
    dayOfWeekId: '', periodId: '',
  });
  const [saving,  setSaving]  = useState(false);
  const [delConf, setDelConf] = useState({ open: false, id: null });
  const [snack,   setSnack]   = useState({ open: false, msg: '', sev: 'success' });

  // ── Conflict check state (edit mode only) ─────────────────────────────────
 const [conflictWarning,   setConflictWarning]   = useState([]); 
  const [checkingConflict,  setCheckingConflict]  = useState(false);

  const showSnack = (msg, sev = 'success') => setSnack({ open: true, msg, sev });

  const checkTeacherLoad = async (teacherId) => {
    const tId = parseInt(teacherId, 10);
    if (!tId || isNaN(tId) || !activeBranchId) { setLoadWarning(''); return; }
    try {
      const res  = await apiGet(`/adv/teacher-load/heatmap?Branch_id=${activeBranchId}`);
      const data = await safeJson(res);
      const list = Array.isArray(data) ? data : [];
      const teacher = list.find(t => Number(t.teacherId) === tId);
      if (teacher) {
        const weeklyLoad = teacher.weeklyLoad || 0;
        const maxPeriods = teacher.maxPeriods || 0;
        const projected  = weeklyLoad + 1; // this new slot
        if (maxPeriods > 0 && projected > maxPeriods) {
          setLoadWarning(
            `${teacher.teacherName} is currently at ${weeklyLoad}/${maxPeriods} periods this week. Adding this slot will take them to ${projected}, exceeding their max load.`
          );
        } else {
          setLoadWarning('');
        }
      } else {
        setLoadWarning('');
      }
    } catch {
      setLoadWarning('');
    }
  };

  const checkTeacherSlot = async (teacherId, daysId, periodId) => {
    const tId = parseInt(teacherId, 10);
    const dId = parseInt(daysId,    10);
    const pId = parseInt(periodId,  10);

    if (!tId || !dId || !pId || isNaN(tId) || isNaN(dId) || isNaN(pId)) {
      setConflictWarning([]);
      setLoadWarning('');
      setCheckingConflict(false);
      return;
    }

   setCheckingConflict(true);
    setConflictWarning([]);
    setLoadWarning('');
    setLoadWarning('');
    try {
      const res  = await apiPost(`/TimeTable/Check_TeacherSlot`, {
        teacherId: tId,
        daysId:    dId,
        periodId:  pId,
      });
      const data      = await safeJson(res);
      // API returns an ARRAY of conflict objects when the teacher is already
      // booked for this day+period, e.g.
      // [{ teacherId, teacherName, subjectName, msg }, ...]
      // Empty array (or non-array) = no conflict.
      const conflicts = Array.isArray(data) ? data : [];
      const messages  = [...new Set(conflicts.map(c => c.msg || c.Msg).filter(Boolean))]; // dedupe
      setConflictWarning(messages);
    } catch {
      setConflictWarning([]);
      setLoadWarning('');
    } finally {
      setCheckingConflict(false);
    }
  };

  // ── Fetch branch list (master users only) ─────────────────────────────────
  const fetchBranches = async () => {
    try {
      const res = await fetch(`${BASE}/Branch/GetBranch`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: tok() },
        body: JSON.stringify({}),
      });
      if (res.ok) {
        const data = await res.json();
        setBranches(data);
        // Auto-fill first branch for master user when branches load
        if (data.length > 0) {
          setSelectedBranchId(data[0].branchId);
        }
      } else {
        console.error('Failed to fetch branches');
      }
    } catch (err) {
      console.error('Error fetching branches:', err);
    }
  };

  useEffect(() => {
  if (ttFromDate) {
    setTtToDate(getSaturdayOfWeek(ttFromDate));
  }
}, [ttFromDate]);

  // ── On mount: detect master user, load branches or read from session ──────
  useEffect(() => {
    const isMasterUser = sessionStorage.getItem('is_Master') === 'true';
    setIsMaster(isMasterUser);

    if (isMasterUser) {
      // fetchBranches sets selectedBranchId → triggers the main data load
      fetchBranches();
    }
    // Non-master: branchId already in session, activeBranchId resolves it
  }, []);

  // ── Init: classes + days + periods + teacher lists ────────────────────────
  // Runs whenever activeBranchId becomes available or changes (branch switch).
  // GetDaysOfWeek and GetPeriod have NO branchId param per Swagger — called as-is.
  // ddlClassName and GetTeacher accept branchId in POST body.
  useEffect(() => {
    // Guard: don't fetch until we have a valid branchId
    if (!activeBranchId) return;

    const init = async () => {
      setLoadingDropdowns(true);
      // Reset dependent selections when branch changes
      setSelClass('');
      setSelSection('');
      setSelTeacher('');
      setClassTT([]);
      setTeacherTT([]);
      try {
        const [clsRes, dayRes, perRes, tchRes] = await Promise.all([
          // POST /Enquiry/ddlClassName — branchId in request body
          apiPost(`/Enquiry/ddlClassName`, { branchId: activeBranchId }),
          // POST /TimeTable/GetDaysOfWeek — no parameters per Swagger
          apiPost(`/TimeTable/GetDaysOfWeek`),
          // POST /TimeTable/GetPeriod — no parameters per Swagger
          apiPost(`/TimeTable/GetPeriod`),
          // POST /AssignmentCreate/GetTeacher — Branch_id as query param (confirmed via Swagger).
          // FIX: Branch_id must be passed as a query param, NOT in the request body.
          // The body approach was silently ignored by the API, causing the teacher
          // dropdown to return all teachers across all branches instead of the selected branch.
          apiPost(`/AssignmentCreate/GetTeacher?Branch_id=${activeBranchId}`),
        ]);

        const cls = await safeJson(clsRes);
        setClasses(Array.isArray(cls) ? cls : []);

        const day = await safeJson(dayRes);
        // Filter out Sunday (dayName === 'Sunday')
        setDays(Array.isArray(day)
          ? day.filter(d => (d.dayName || d.DayName || '').toLowerCase() !== 'sunday')
          : []);

        const per = await safeJson(perRes);
        // Sort periods by sequenceNo so they display in order
        const perArr = Array.isArray(per) ? per : [];
        perArr.sort((a, b) => (a.sequenceNo || 0) - (b.sequenceNo || 0));
        setPeriods(perArr);

        const tch = await safeJson(tchRes);
        const tchArr = Array.isArray(tch) ? tch : [];
        setAllTeachers(tchArr);
        // formTeachers mirrors allTeachers — used in the Add/Edit modal teacher dropdown
        // setFormTeachers(tchArr);
      } catch (err) {
        console.error('Init error', err);
        showSnack('Failed to load dropdown data', 'error');
      } finally {
        setLoadingDropdowns(false);
      }
    };
    init();
  // Re-run only when selectedBranchId changes (master users switching branches).
  // For non-master users activeBranchId is stable (from sessionStorage) so this
  // fires exactly once on mount — no double-fetch risk.
  }, [selectedBranchId]);

  // ── Sections when class changes ───────────────────────────────────────────
  // POST /Teacher/ddlSection_clsId — branchId in request body
  useEffect(() => {
    if (!selClass || !activeBranchId) return;
    const load = async () => {
      try {
        const res  = await apiPost(`/Teacher/ddlSection_clsId`, {
          teacherId: empId(),
          classId: selClass,
          branchId: activeBranchId,
        });
        const data = await safeJson(res);
        setSections(Array.isArray(data) ? data : []);
      } catch { setSections([]); }
      setSelSection('');
      setClassTT([]);
    };
    load();
  }, [selClass]);

  // ── Load class-view timetable ─────────────────────────────────────────────
  // GET /adv/timetable/class-view — Branch_id as query param (adv/ pattern)
 const loadClassTT = useCallback(async () => {
    if (!selClass || !selSection) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({
        classId: selClass, sectionId: selSection, Branch_id: activeBranchId,
      });
      if (ttFromDate) params.set('fromDate', ttFromDate);
      if (ttToDate)   params.set('toDate',   ttToDate);
      const res  = await apiGet(`/adv/timetable/class-view?${params}`);
      const data = await safeJson(res);
      setClassTT(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      showSnack('Error loading timetable', 'error');
    } finally { setLoading(false); }
  }, [selClass, selSection, activeBranchId, ttFromDate, ttToDate]);

  // loadClassTT is in the dep array for the same reason as loadTeacherTT —
  // it captures activeBranchId via useCallback, so when the branch changes its
  // reference updates and this effect must re-run to fetch the correct branch data.
  useEffect(() => { if (selClass && selSection) loadClassTT(); }, [selSection, loadClassTT]);

  // ── Load teacher-view timetable ───────────────────────────────────────────
  // GET /adv/timetable/teacher-view — teacherId + Branch_id as query params.
  // FIX: activeBranchId is now included as Branch_id so the teacher timetable
  // is always fetched for the correct branch, both on initial load and after
  // a master user switches branches (selTeacher reset clears stale data).
  const loadTeacherTT = useCallback(async () => {
    if (!selTeacher || !activeBranchId) return;
    setLoading(true);
    try {
const params = new URLSearchParams({ teacherId: selTeacher, Branch_id: activeBranchId });
if (ttFromDate) params.set('fromDate', ttFromDate);
if (ttToDate)   params.set('toDate',   ttToDate);
const res  = await apiGet(`/adv/timetable/teacher-view?${params}`);      const data = await safeJson(res);
      setTeacherTT(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      showSnack('Error loading teacher timetable', 'error');
    } finally { setLoading(false); }
  }, [selTeacher, activeBranchId, ttFromDate, ttToDate]);

  // Trigger teacher timetable load whenever selTeacher OR activeBranchId changes.
  // loadTeacherTT is in the dep array because it's a useCallback that captures
  // activeBranchId — when the branch changes, loadTeacherTT gets a new reference,
  // and including it here ensures the effect re-runs with the correct branchId.
  // This is the key fix: without loadTeacherTT in deps, a branch switch would
  // never re-fetch because selTeacher itself didn't change.
  useEffect(() => { if (selTeacher) loadTeacherTT(); }, [selTeacher, loadTeacherTT]);

  // ── Subjects for form ─────────────────────────────────────────────────────
  // POST /AssignmentCreate/Subjectddl — branchId in request body
  const loadFormSubjects = async (classId, sectionId, teacherId) => {
    if (!classId || !sectionId || !teacherId) { setFormSubjects([]); return; }
    try {
      const res  = await apiPost(`/AssignmentCreate/Subjectddl`, {
        classId,
        sectionId,
        teacherId,
        branchId: activeBranchId,
      });
      const data = await safeJson(res);
      setFormSubjects(Array.isArray(data) ? data : []);
    } catch { setFormSubjects([]); }
  };

  // ── Slot lookup ───────────────────────────────────────────────────────────
  // Class view  → one slot per cell (first match). Duplicate entries in DB
  //               are a data conflict; we show only the first.
  // Teacher view → multiple slots per cell are valid (same period, diff classes).
  // Both sides are stringified to avoid number/string mismatches.
  const getSlots = useCallback((dayId, periodId) => {
    const data = tab === 0 ? classTT : teacherTT;
    const matches = data.filter(s =>
      String(s.daysId || s.DaysId || s.dayId || '') === String(dayId) &&
      String(s.periodId || s.PeriodId || '')         === String(periodId)
    );
    // Always cap at 1 — duplicate entries are a DB conflict, not valid data
    return matches.slice(0, 1);
  }, [classTT, teacherTT, tab]);


  const loadClassTeachers = async (classId, sectionId) => {
    if (!classId || !sectionId) { setFormTeachers([]); return; }
    try {
      const res  = await apiPost(`/ClassTeacherMapping/FetchClassTeacher`, {
        classId:   parseInt(classId, 10),
        sectionId: parseInt(sectionId, 10),
      });
      const data = await safeJson(res);
      const rows = Array.isArray(data) ? data : [];
      const seen = new Set();
      const mapped = [];
      rows.forEach(r => {
        const tId = r.teacherId || r.TeacherId;
        if (!tId || seen.has(tId)) return;
        seen.add(tId);
        const match = allTeachers.find(t => String(t.employeeId || t.EmployeeId) === String(tId));
        mapped.push({
          employeeId:   tId,
          employeeName: match ? (match.employeeName || match.EmployeeName) : `Teacher #${tId}`,
        });
      });
      setFormTeachers(mapped);
    } catch { setFormTeachers([]); }
  };
  // ── Handle Add ────────────────────────────────────────────────────────────
  const handleAdd = (dayId, periodId) => {
    setEditSlot(null);
    setFormSubjects([]);
    setConflictWarning([]);
    setLoadWarning('');
    setCheckingConflict(false);
   loadClassTeachers(selClass, selSection); 
    setForm({
      timetableId: 0,
      classId:     selClass,
      sectionId:   selSection,
      dayOfWeekId: dayId,
      periodId,
      subjectId:   '',
      teacherId:   '',
      roomNo:      '',
    });
    setModalOpen(true);
  };

  // ── Handle Edit ───────────────────────────────────────────────────────────
  const handleEdit = (slot) => {
    setEditSlot(slot);
    setConflictWarning([]);
    setLoadWarning('');
    setCheckingConflict(false);
    const cId = slot.classId   || slot.ClassId   || selClass;
    const sId = slot.sectionId || slot.SectionId || selSection;
    const tId = slot.teacherId || slot.TeacherId || '';
    const dId = slot.daysId    || slot.DaysId    || slot.dayId || slot.dayOfWeekId || '';
    const pId = slot.periodId  || slot.PeriodId  || '';
    loadFormSubjects(cId, sId, tId);
    loadClassTeachers(cId, sId);
    setForm({
      timetableId: slot.timetableId || slot.TimeTableId || 0,
      classId:     cId,
      sectionId:   sId,
      subjectId:   slot.subjectId  || slot.SubjectId  || '',
      teacherId:   tId,
      roomNo:      (slot.roomId && slot.roomId !== 0) ? slot.roomId : (slot.roomNo || ''),
      dayOfWeekId: dId,
      periodId:    pId,
    });
    checkTeacherSlot(tId, dId, pId);
    checkTeacherLoad(tId);  
    setModalOpen(true);
  };

  // ── Save slot ─────────────────────────────────────────────────────────────
  // POST /adv/timetable/save and PUT /adv/timetable/update/:id
  // These adv/ endpoints follow the same pattern — no branchId in schema per Swagger
  const handleSave = async () => {
    setSaving(true);
    try {
      const body = {
        classId:     parseInt(form.classId,     10),
        sectionId:   parseInt(form.sectionId,   10),
        subjectId:   parseInt(form.subjectId,   10),
        teacherId:   parseInt(form.teacherId,   10),
        roomNo:      form.roomNo ? parseInt(form.roomNo, 10) : 0,
        dayOfWeekId: parseInt(form.dayOfWeekId, 10),
        periodId:    parseInt(form.periodId,    10),
      };

      let res;
      if (editSlot && form.timetableId) {
        res = await apiPut(`/adv/timetable/update/${form.timetableId}`, body);
      } else {
        res = await apiPost(`/adv/timetable/save`, body);
      }

      const data   = await safeJson(res);
      const status = data?.Status || data?.status || '';
      const msg    = data?.Msg    || data?.msg    || '';

      if (status === 'Succeed!' || res.ok) {
        showSnack(msg || (editSlot ? 'Slot updated!' : 'Slot added!'), 'success');
        setModalOpen(false);
        setConflictWarning([]);
        setLoadWarning('');
        setCheckingConflict(false);
        tab === 0 ? loadClassTT() : loadTeacherTT();
      } else {
        showSnack(msg || 'Save failed. Please check for conflicts.', 'error');
      }
    } catch (err) {
      console.error(err);
      showSnack('Error saving slot', 'error');
    } finally {
      setSaving(false);
    }
  };

  // ── Delete slot ───────────────────────────────────────────────────────────
  const handleDelete = async () => {
    try {
      const res    = await apiDel(`/adv/timetable/delete/${delConf.id}`);
      const data   = await safeJson(res);
      const status = data?.Status || data?.status || '';
      if (status === 'Succeed!' || res.ok) {
        showSnack('Slot deleted');
        setDelConf({ open: false, id: null });
        tab === 0 ? loadClassTT() : loadTeacherTT();
      } else {
        showSnack(data?.Msg || data?.msg || 'Delete failed', 'error');
      }
    } catch { showSnack('Error deleting slot', 'error'); }
  };

  // ── PDF download ──────────────────────────────────────────────────────────
  const handleDownloadPDF = () => {
    const activeSlots = tab === 0 ? classTT : teacherTT;
    const titleLabel  = tab === 0
      ? `Timetable — ${classes.find(c => String(c.classId || c.ClassId) === String(selClass))?.className || ''} ${sections.find(s => String(s.sectionId || s.SectionId) === String(selSection))?.sectionName || ''}`
      : `Timetable — ${allTeachers.find(t => String(t.employeeId || t.EmployeeId) === String(selTeacher))?.employeeName || 'Teacher'}`;
    generatePDF({ title: titleLabel, days, periods, slots: activeSlots });
  };

  // ── Derived stats ─────────────────────────────────────────────────────────
  const activeData   = tab === 0 ? classTT : teacherTT;
  const filledSlots  = activeData.length;
  const totalSlots   = days.length * periods.length;
  const fillPct      = totalSlots > 0 ? Math.round((filledSlots / totalSlots) * 100) : 0;
  const uniqueSubjs  = [...new Set(activeData.map(s => s.subjectName).filter(Boolean))].length;
  const uniqueTeachs = [...new Set(activeData.map(s => s.teacherName).filter(Boolean))].length;

  const hasData = tab === 0 ? (selClass && selSection) : selTeacher;

  // ─────────────────────────────────────────────────────────────────────────
  // Render timetable grid
  // ─────────────────────────────────────────────────────────────────────────
  const renderGrid = () => (
    <Box sx={{ overflowX: 'auto', pb: 1 }}>
      <Box sx={{ minWidth: Math.max(700, 136 + days.length * 140) }}>

        {/* Header row */}
        <Box sx={{ display: 'grid', gridTemplateColumns: `136px repeat(${days.length}, 1fr)`, gap: '6px', mb: '6px' }}>
          <Box sx={{
            bgcolor: C.surface, borderRadius: '10px',
            border: `1px solid ${C.border}`,
            p: '10px 12px', display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Typography sx={{ fontWeight: 700, color: C.muted, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Period ╲ Day
            </Typography>
          </Box>
          {days.map(d => (
            <Box key={getDayId(d)} sx={{
              bgcolor: C.navy, borderRadius: '10px',
              p: '10px 8px', display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Typography sx={{ fontWeight: 700, color: '#fff', fontSize: '0.78rem', letterSpacing: 0.4 }}>
                {getDayLabel(d)}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* Period rows */}
        {periods.map(period => {
          const pId    = getPerId(period);
          const pLabel = getPerLabel(period);
          const pTime  = getPerTime(period);
          return (
            <Box key={pId} sx={{ display: 'grid', gridTemplateColumns: `136px repeat(${days.length}, 1fr)`, gap: '6px', mb: '6px' }}>
              {/* Period label cell */}
              <Box sx={{
                bgcolor: C.surface, border: `1px solid ${C.border}`,
                borderRadius: '10px', p: '8px 10px',
                display: 'flex', flexDirection: 'column', justifyContent: 'center',
              }}>
                <Typography sx={{ fontWeight: 700, color: C.navy, fontSize: '0.74rem' }}>
                  {pLabel}
                </Typography>
                {pTime && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, mt: 0.4 }}>
                    <TimeIcon sx={{ fontSize: 10, color: C.faint }} />
                    <Typography sx={{ color: C.faint, fontSize: '0.62rem' }}>{pTime}</Typography>
                  </Box>
                )}
              </Box>

              {/* Slot cells — one per day */}
              {days.map(day => {
                const dId       = getDayId(day);
                const cellSlots = getSlots(dId, pId);
                return (
                  <SlotCell
                    key={dId}
                    slots={cellSlots}
                    dayId={dId}
                    periodId={pId}
                    onEdit={handleEdit}
                    onDelete={id => setDelConf({ open: true, id })}
                    onAdd={handleAdd}
                    canAdd={tab === 0}
                    canEdit={tab === 0}
                    canDelete={true}
                    // In teacher-view show class name on each card
                    showClass={tab === 1}
                  />
                );
              })}
            </Box>
          );
        })}
      </Box>
    </Box>
  );

  // ─────────────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ bgcolor: C.surface, minHeight: '100vh', pb: 5 }}>

      {/* ── Page header ── */}
      <Box sx={{
        background: `linear-gradient(135deg, ${C.navy} 0%, ${C.navyMid} 100%)`,
        py: 3, px: { xs: 2, md: 4 },
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        flexWrap: 'wrap', gap: 1.5,
      }}>
        <Box>
          <Typography variant="h5" sx={{ color: '#fff', fontWeight: 800, letterSpacing: -0.4 }}>
            Timetable Manager
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', mt: 0.3, fontSize: '0.82rem' }}>
            View, create and manage class &amp; teacher schedules
          </Typography>
        </Box>

        {/* ── Right side: Branch dropdown (master only) + stats chips + PDF ── */}
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>

          {/* ── Branch Dropdown — visible only for Master/Admin users ── */}
          {isMaster && (
            <FormControl size="small" sx={{ width: 220 }}>
              <InputLabel
                sx={{
                  color: 'rgba(255,255,255,0.6)',
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  letterSpacing: '0.6px',
                  textTransform: 'uppercase',
                  top: '-2px',
                  '&.Mui-focused': { color: '#64B5F6' },
                  '&.MuiFormLabel-filled': { color: 'rgba(255,255,255,0.7)' },
                }}
              >
                Branch
              </InputLabel>
              <Select
                value={selectedBranchId || ''}
                onChange={(e) => {
                  setSelectedBranchId(e.target.value);
                  // Reset all selections when branch changes
                  setSelClass(''); setSelSection(''); setSelTeacher('');
                  setClassTT([]); setTeacherTT([]);
                }}
                label="Branch"
                startAdornment={
                  <InputAdornment position="start">
                    <LocationOnIcon sx={{ fontSize: 16, color: 'rgba(255,255,255,0.5)', ml: 0.5 }} />
                  </InputAdornment>
                }
                sx={{
                  color: '#ffffff',
                  fontWeight: 500,
                  fontSize: '0.875rem',
                  letterSpacing: '0.3px',
                  backgroundColor: 'rgba(255,255,255,0.07)',
                  backdropFilter: 'blur(8px)',
                  borderRadius: '8px',
                  transition: 'background-color 0.2s ease, box-shadow 0.2s ease',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255,255,255,0.15)',
                    borderWidth: '1px',
                    transition: 'border-color 0.2s ease',
                  },
                  '&:hover': {
                    backgroundColor: 'rgba(255,255,255,0.11)',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(255,255,255,0.35)',
                    },
                  },
                  '&.Mui-focused': {
                    backgroundColor: 'rgba(255,255,255,0.1)',
                    boxShadow: '0 0 0 3px rgba(100,181,246,0.18)',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#64B5F6',
                      borderWidth: '1.5px',
                    },
                  },
                  '& .MuiSelect-icon': {
                    color: 'rgba(255,255,255,0.5)',
                    transition: 'color 0.2s ease',
                  },
                  '&.Mui-focused .MuiSelect-icon': { color: '#64B5F6' },
                  '& .MuiSelect-select': { py: 1.1 },
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      mt: 0.5,
                      borderRadius: '10px',
                      border: '1px solid rgba(11,31,61,0.12)',
                      boxShadow: '0 8px 32px rgba(11,31,61,0.18), 0 2px 8px rgba(0,0,0,0.08)',
                      overflow: 'hidden',
                      '& .MuiList-root': { py: 0.5 },
                    },
                  },
                }}
              >
                {branches.map((branch) => (
                  <MenuItem
                    key={branch.branchId}
                    value={branch.branchId}
                    sx={{
                      fontSize: '0.875rem',
                      fontWeight: 500,
                      color: '#0B1F3D',
                      px: 2, py: 1.1, mx: 0.5,
                      borderRadius: '6px',
                      transition: 'background-color 0.15s ease',
                      '&:hover': { backgroundColor: 'rgba(11,31,61,0.07)' },
                      '&.Mui-selected': {
                        backgroundColor: 'rgba(25,118,210,0.1)',
                        color: '#1565C0',
                        fontWeight: 600,
                        '&:hover': { backgroundColor: 'rgba(25,118,210,0.15)' },
                      },
                    }}
                  >
                    {branch.branchName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* Stats chips + PDF button — only when data is loaded */}
          {activeData.length > 0 && (
            <>
              <Chip
                label={`${filledSlots} / ${totalSlots} Slots (${fillPct}%)`}
                size="small"
                sx={{ bgcolor: 'rgba(255,255,255,0.13)', color: '#fff', fontWeight: 600, fontSize: '0.72rem' }}
              />
              <Chip
                label={`${uniqueSubjs} Subject${uniqueSubjs !== 1 ? 's' : ''}`}
                size="small"
                sx={{ bgcolor: 'rgba(25,118,210,0.35)', color: '#90CAF9', fontWeight: 600, fontSize: '0.72rem' }}
              />
              {tab === 0 && (
                <Chip
                  label={`${uniqueTeachs} Teacher${uniqueTeachs !== 1 ? 's' : ''}`}
                  size="small"
                  sx={{ bgcolor: 'rgba(46,125,50,0.35)', color: '#A5D6A7', fontWeight: 600, fontSize: '0.72rem' }}
                />
              )}
              <Tooltip title="Download timetable as PDF">
                <Button
                  size="small"
                  startIcon={<PdfIcon sx={{ fontSize: 16 }} />}
                  onClick={handleDownloadPDF}
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.15)', color: '#fff',
                    fontWeight: 700, fontSize: '0.74rem', borderRadius: '8px',
                    border: '1px solid rgba(255,255,255,0.3)',
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' },
                    textTransform: 'none', px: 1.5, py: 0.5,
                  }}
                >
                  Download PDF
                </Button>
              </Tooltip>
            </>
          )}
        </Box>
      </Box>

      <Container maxWidth="xl" sx={{ px: { xs: 1.5, md: 3 }, mt: 3 }}>

        {/* ── Controls card ── */}
        <Card sx={{ borderRadius: '16px', boxShadow: '0 2px 16px rgba(11,31,61,0.08)', border: `1px solid ${C.border}`, mb: 3 }}>
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            sx={{
              borderBottom: `1px solid ${C.border}`, px: 2,
              '& .MuiTab-root': { fontWeight: 700, textTransform: 'none', minHeight: 50, fontSize: '0.87rem' },
              '& .Mui-selected': { color: C.navy },
              '& .MuiTabs-indicator': { backgroundColor: C.navy, height: 3, borderRadius: 2 },
            }}
          >
            <Tab icon={<SchoolIcon sx={{ fontSize: 18 }} />} label="Class View"   iconPosition="start" />
            <Tab icon={<PersonIcon sx={{ fontSize: 18 }} />} label="Teacher View" iconPosition="start" />
          </Tabs>

          <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
            {tab === 0 ? (
              <Grid container spacing={2} alignItems="center">
  <Grid item xs={12} sm={3}>
    <FormControl fullWidth size="small" disabled={loadingDropdowns}>
      <InputLabel>Class</InputLabel>
      <Select value={selClass} label="Class"
        onChange={e => setSelClass(e.target.value)}
        sx={{ borderRadius: '10px' }}
      >
        {classes.map(c => (
          <MenuItem key={c.classId || c.ClassId} value={c.classId || c.ClassId}>
            {c.className || c.ClassName}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  </Grid>
  <Grid item xs={12} sm={3}>
    <FormControl fullWidth size="small" disabled={!selClass || sections.length === 0}>
      <InputLabel>Section</InputLabel>
      <Select value={selSection} label="Section"
        onChange={e => setSelSection(e.target.value)}
        sx={{ borderRadius: '10px' }}
      >
        {sections.map(s => (
          <MenuItem key={s.sectionId || s.SectionId} value={s.sectionId || s.SectionId}>
            {s.sectionName || s.SectionName}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  </Grid>
  <Grid item xs={6} sm={2}>
    <TextField
      label="From Date" type="date" size="small" fullWidth
      value={ttFromDate}
      onChange={e => setTtFromDate(e.target.value)}
      InputLabelProps={{ shrink: true }}
      sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
    />
  </Grid>
 <Grid item xs={6} sm={2}>
    <TextField
      label="To Date" type="date" size="small" fullWidth
      value={ttToDate}
      disabled
      InputLabelProps={{ shrink: true }}
      sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
    />
  </Grid>
  <Grid item xs={12} sm={2} sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="outlined" startIcon={<RefreshIcon />}
                    onClick={loadClassTT}
                    disabled={!selClass || !selSection}
                    sx={{ borderRadius: '10px', fontWeight: 600, borderColor: C.border, color: C.navy, '&:hover': { borderColor: C.navy, bgcolor: C.blueSoft } }}
                  >
                    Reload
                  </Button>
                  {activeData.length > 0 && (
                    <Button
                      variant="outlined" startIcon={<PdfIcon />}
                      onClick={handleDownloadPDF}
                      sx={{ borderRadius: '10px', fontWeight: 600, borderColor: C.border, color: C.red, '&:hover': { borderColor: C.red, bgcolor: '#FFEBEE' } }}
                    >
                      PDF
                    </Button>
                  )}
                </Grid>
              </Grid>
            ) : (
             <Grid container spacing={2} alignItems="center">
  <Grid item xs={12} sm={4}>
    <FormControl fullWidth size="small" disabled={loadingDropdowns}>
      <InputLabel>Select Teacher</InputLabel>
      <Select value={selTeacher} label="Select Teacher"
        onChange={e => setSelTeacher(e.target.value)}
        sx={{ borderRadius: '10px' }}
      >
        {allTeachers.map(t => (
          <MenuItem key={t.employeeId || t.EmployeeId} value={t.employeeId || t.EmployeeId}>
            {t.employeeName || t.EmployeeName}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  </Grid>
  <Grid item xs={6} sm={2}>
    <TextField
      label="From Date" type="date" size="small" fullWidth
      value={ttFromDate}
      onChange={e => setTtFromDate(e.target.value)}
      InputLabelProps={{ shrink: true }}
      sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
    />
  </Grid>
 <Grid item xs={6} sm={2}>
    <TextField
      label="To Date" type="date" size="small" fullWidth
      value={ttToDate}
      disabled
      InputLabelProps={{ shrink: true }}
      sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
    />
  </Grid>
  <Grid item sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="outlined" startIcon={<RefreshIcon />}
                    onClick={loadTeacherTT}
                    disabled={!selTeacher}
                    sx={{ borderRadius: '10px', fontWeight: 600, borderColor: C.border, color: C.navy, '&:hover': { borderColor: C.navy, bgcolor: C.blueSoft } }}
                  >
                    Reload
                  </Button>
                  {activeData.length > 0 && (
                    <Button
                      variant="outlined" startIcon={<PdfIcon />}
                      onClick={handleDownloadPDF}
                      sx={{ borderRadius: '10px', fontWeight: 600, borderColor: C.border, color: C.red, '&:hover': { borderColor: C.red, bgcolor: '#FFEBEE' } }}
                    >
                      PDF
                    </Button>
                  )}
                </Grid>
              </Grid>
            )}
          </CardContent>
        </Card>

        {/* ── Timetable grid card ── */}
        <Card sx={{ borderRadius: '16px', boxShadow: '0 2px 16px rgba(11,31,61,0.08)', border: `1px solid ${C.border}` }}>

          {/* Colour legend */}
          {activeData.length > 0 && (
            <Box sx={{ px: 2.5, pt: 2, pb: 0, display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: C.muted, mr: 0.5, textTransform: 'uppercase', letterSpacing: 0.4, fontSize: '0.68rem' }}>
                Subjects:
              </Typography>
              {[...new Set(activeData.map(s => s.subjectName).filter(Boolean))].map(name => {
                const pal = getPalette(name);
                return (
                  <Box key={name} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ width: 10, height: 10, borderRadius: '3px', bgcolor: pal.border }} />
                    <Typography variant="caption" sx={{ fontSize: '0.7rem', color: C.text, fontWeight: 500 }}>
                      {name}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          )}

          <CardContent sx={{ p: { xs: 1.5, md: 2.5 }, pt: activeData.length > 0 ? '12px !important' : undefined }}>
            {loading ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 10, gap: 2 }}>
                <CircularProgress sx={{ color: C.navy }} size={40} thickness={4} />
                <Typography sx={{ color: C.muted, fontSize: '0.87rem' }}>Loading timetable…</Typography>
              </Box>
            ) : hasData ? (
              renderGrid()
            ) : (
              <Box sx={{ py: 10, textAlign: 'center' }}>
                <CalendarIcon sx={{ fontSize: 60, color: '#CBD5E1', mb: 2 }} />
                <Typography sx={{ color: C.muted, fontWeight: 600, fontSize: '1rem' }}>
                  {tab === 0
                    ? 'Select a class and section to view the timetable'
                    : 'Select a teacher to view their schedule'}
                </Typography>
                <Typography sx={{ color: C.faint, fontSize: '0.82rem', mt: 0.8 }}>
                  Use the dropdowns above to get started
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      </Container>

      {/* ══════════════════════════════════════════════════════════════════
          Add / Edit Modal
      ══════════════════════════════════════════════════════════════════ */}
      <Dialog
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setConflictWarning([]);
          setLoadWarning('');
          setCheckingConflict(false);
        }}
        maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: '16px', overflow: 'hidden' } }}
      >
        <DialogTitle sx={{
          bgcolor: C.navy, color: '#fff', fontWeight: 700, py: 2,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <span>{editSlot ? '✏️  Edit Timetable Slot' : '➕  Add Timetable Slot'}</span>
          <IconButton size="small"
            onClick={() => {
              setModalOpen(false);
              setConflictWarning([]);
              setLoadWarning('');
              setCheckingConflict(false);
            }}
            sx={{ color: 'rgba(255,255,255,0.7)', '&:hover': { color: '#fff', bgcolor: 'rgba(255,255,255,0.12)' } }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: '22px !important', pb: 1 }}>
          <Grid container spacing={2}>

            {/* Teacher */}
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel>Teacher *</InputLabel>
                <Select value={form.teacherId} label="Teacher *"
                  onChange={e => {
                    const tId = e.target.value;
                    setForm(f => ({ ...f, teacherId: tId, subjectId: '' }));
                    loadFormSubjects(form.classId, form.sectionId, tId);
                    // ── Conflict check (edit mode only) ──
                   checkTeacherSlot(tId, form.dayOfWeekId, form.periodId);
                   checkTeacherLoad(tId);
                  }}
                  sx={{ borderRadius: '10px' }}
                >
                  <MenuItem value=""><em>Select teacher…</em></MenuItem>
                  {formTeachers.map(t => (
                    <MenuItem key={t.employeeId || t.EmployeeId} value={t.employeeId || t.EmployeeId}>
                      {t.employeeName || t.EmployeeName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Subject */}
            <Grid item xs={12}>
              <FormControl fullWidth size="small" disabled={!form.teacherId}>
                <InputLabel>Subject *</InputLabel>
                <Select value={form.subjectId} label="Subject *"
                  onChange={e => setForm(f => ({ ...f, subjectId: e.target.value }))}
                  sx={{ borderRadius: '10px' }}
                >
                  <MenuItem value=""><em>Select subject…</em></MenuItem>
                  {formSubjects.map(s => (
                    <MenuItem key={s.subjectId || s.SubjectId} value={s.subjectId || s.SubjectId}>
                      {s.subjectName || s.SubjectName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Day */}
            <Grid item xs={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Day *</InputLabel>
                <Select value={form.dayOfWeekId} label="Day *"
                  onChange={e => {
                    const newDayId        = e.target.value;
                    const currentTeacher  = form.teacherId;
                    const currentPeriod   = form.periodId;
                    setForm(f => ({ ...f, dayOfWeekId: newDayId }));
                   checkTeacherSlot(currentTeacher, newDayId, currentPeriod);
                  }}
                  sx={{ borderRadius: '10px' }}
                >
                  <MenuItem value=""><em>Select…</em></MenuItem>
                  {days.map(d => (
                    <MenuItem key={getDayId(d)} value={getDayId(d)}>{getDayLabel(d)}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Period */}
            <Grid item xs={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Period *</InputLabel>
                <Select value={form.periodId} label="Period *"
                  onChange={e => {
                    const newPeriodId     = e.target.value;
                    const currentTeacher  = form.teacherId;
                    const currentDay      = form.dayOfWeekId;
                    setForm(f => ({ ...f, periodId: newPeriodId }));
                     checkTeacherSlot(currentTeacher, currentDay, newPeriodId);
                  }}
                  sx={{ borderRadius: '10px' }}
                >
                  <MenuItem value=""><em>Select…</em></MenuItem>
                  {periods.map(p => (
                    <MenuItem key={getPerId(p)} value={getPerId(p)}>
                      {getPerLabel(p)}{getPerTime(p) ? ` (${getPerTime(p)})` : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Room No */}
            <Grid item xs={6}>
              <TextField size="small" fullWidth label="Room No" required
                type="number" value={form.roomNo}
                onChange={e => setForm(f => ({ ...f, roomNo: e.target.value }))}
                InputProps={{ sx: { borderRadius: '10px' } }}
              />
            </Grid>
          </Grid>

         {/* ── Conflict warning banner (edit mode only, shown when conflict detected) ── */}
          {conflictWarning.length > 0 && ( 
            <Box sx={{
              mt: 2, p: 1.5,
              bgcolor: '#FFF3E0', borderRadius: '10px',
              border: `1.5px solid ${C.red}`,
              display: 'flex', flexDirection: 'column', gap: 0.75,
              animation: 'conflictBlink 1s ease-in-out infinite',
              '@keyframes conflictBlink': {
                '0%, 100%': { opacity: 1 },
                '50%': { opacity: 0.4 },
              },
            }}>
              {conflictWarning.map((msg, i) => (
                <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <WarningIcon sx={{ fontSize: 16, color: C.red, mt: 0.15, flexShrink: 0 }} />
                  <Typography variant="caption" sx={{ color: C.red, fontWeight: 700, fontSize: '0.72rem' }}>
                    {msg}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}


          {/* ── Teacher load warning banner (soft warning, doesn't block save) ── */}
          {loadWarning && (
            <Box sx={{
              mt: 2, p: 1.5,
              bgcolor: '#FFFBEB', borderRadius: '10px',
              border: `1.5px solid ${C.orange}`,
              display: 'flex', alignItems: 'flex-start', gap: 1,
            }}>
              <WarningIcon sx={{ fontSize: 16, color: C.orange, mt: 0.15, flexShrink: 0 }} />
              <Typography variant="caption" sx={{ color: C.orange, fontWeight: 700, fontSize: '0.72rem' }}>
                {loadWarning}
              </Typography>
            </Box>
          )}

          {/* ── Checking indicator (edit mode only, shown while API call is in-flight) ── */}
         {checkingConflict && conflictWarning.length === 0 && (
            <Box sx={{
              mt: 2, p: 1.5,
              bgcolor: C.surface, borderRadius: '10px',
              border: `1px solid ${C.border}`,
              display: 'flex', alignItems: 'center', gap: 1,
            }}>
              <CircularProgress size={13} sx={{ color: C.muted }} />
              <Typography variant="caption" sx={{ color: C.muted, fontWeight: 600, fontSize: '0.72rem' }}>
                Checking for conflicts…
              </Typography>
            </Box>
            
          )}

          {/* ── Static info tip (add mode only) ── */}
         {/* ── Static info tip (add mode only) ── */}
          {!editSlot && (
            <Box sx={{ mt: 2, p: 1.5, bgcolor: '#EFF6FF', borderRadius: '10px', border: '1px solid #BFDBFE' }}>
              <Typography variant="caption" sx={{ color: '#1D4ED8', fontWeight: 600, fontSize: '0.7rem' }}>
                💡 The system will reject the slot if a conflict is detected (same teacher or class already booked for this day + period).
              </Typography>
            </Box>
          )}

          {/* ── Teacher-list note (shown in both Add and Edit mode) ── */}
          <Typography variant="caption" sx={{ color: C.muted, fontSize: '0.68rem', display: 'block', mt: 1 }}>
            Note: some teachers may not appear here if they aren't assigned to this class. You can assign them via Class Teacher Mapping inside Classroom Management.
          </Typography>
        </DialogContent>

        <DialogActions sx={{ p: '16px 24px', gap: 1 }}>
          <Button
            onClick={() => {
              setModalOpen(false);
              setConflictWarning([]);
              setLoadWarning('');
              setCheckingConflict(false);
            }}
            variant="outlined"
            sx={{ borderRadius: '10px', fontWeight: 600, borderColor: C.border, color: C.muted }}
          >
            Cancel
          </Button>
          <Button onClick={handleSave} variant="contained"
           disabled={
              saving ||
              checkingConflict ||
              conflictWarning.length > 0 ||
              !form.roomNo || !form.subjectId || !form.teacherId || !form.dayOfWeekId || !form.periodId
            }
            startIcon={
              saving || checkingConflict
                ? <CircularProgress size={16} color="inherit" />
                : <CheckIcon />
            }
            sx={{ borderRadius: '10px', fontWeight: 700, bgcolor: C.navy, '&:hover': { bgcolor: C.navyMid } }}
          >
            {saving ? 'Saving…' : checkingConflict ? 'Checking…' : editSlot ? 'Update Slot' : 'Save Slot'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ══════════════════════════════════════════════════════════════════
          Delete Confirm Dialog
      ══════════════════════════════════════════════════════════════════ */}
      <Dialog
        open={delConf.open}
        onClose={() => setDelConf({ open: false, id: null })}
        maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: '16px', overflow: 'hidden' } }}
      >
        <DialogTitle sx={{ bgcolor: '#B71C1C', color: '#fff', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon /> Delete Confirmation
        </DialogTitle>
      <DialogContent sx={{ pt: '20px !important' }}>
  <Typography>
    Are you sure you want to delete this timetable slot? This action <strong>cannot be undone</strong>.
  </Typography>
</DialogContent>
        <DialogActions sx={{ p: '12px 20px', gap: 1 }}>
          <Button onClick={() => setDelConf({ open: false, id: null })} variant="outlined"
            sx={{ borderRadius: '10px', fontWeight: 600 }}>
            Cancel
          </Button>
          <Button onClick={handleDelete} variant="contained" color="error"
            startIcon={<DeleteIcon />}
            sx={{ borderRadius: '10px', fontWeight: 700 }}>
            Yes, Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Snackbar ── */}
      <Snackbar
        open={snack.open}
        autoHideDuration={4500}
        onClose={() => setSnack(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnack(s => ({ ...s, open: false }))}
          severity={snack.sev}
          variant="filled"
          sx={{ width: '100%', borderRadius: '10px', fontWeight: 600 }}
        >
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}